<template>
  <div class="relative w-full">

    <!-- Header -->
    <div class="bg-white shadow-sm border-b px-4 py-3 sticky top-0 z-10">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div class="flex items-center gap-4 w-full sm:w-auto">
          <h1 class="text-xl md:text-2xl font-semibold text-gray-900">Usuarios</h1>
          <label class="inline-flex items-center text-sm text-gray-700">
            <input type="checkbox" v-model="showAll" class="form-checkbox h-4 w-4 text-green-600" />
            <span class="ml-2">Mostrar todos</span>
          </label>
        </div>

        <div class="flex items-center gap-3 w-full sm:w-auto">
          <div class="relative w-full sm:w-64">
            <input v-model="search" type="search" placeholder="Buscar por nombre, email o empresa" class="w-full pl-3 pr-9 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-emerald-300" />
            <svg class="w-4 h-4 absolute right-2 top-2.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/></svg>
          </div>

          <router-link :to="{ path: '/owner/formUsuariosAdmin' }" class="inline-flex items-center text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-md px-3 py-2">
            <svg class="w-4 h-4 inline-block text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
            Agregar
          </router-link>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="mt-4 bg-white shadow-sm">
      <div class="px-4 py-2">
        <div class="overflow-x-auto">
          <div class="max-h-[calc(100vh-240px)] overflow-auto border border-gray-100 rounded">
            <div class="grid gap-3">
              <template v-if="displayedUsuarios.length">
                <div v-for="usuario in displayedUsuarios" :key="usuario.id" class="bg-white rounded shadow-sm p-3 flex items-center justify-between gap-3">
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 truncate">{{ usuario.fullName || usuario.email }}</div>
                    <div class="text-xs text-gray-500 truncate">{{ usuario.email }} · {{ usuario.numeroTlf }}</div>
                    <div class="text-xs text-gray-400 mt-1">#{{ usuario.tenantId }} - {{ usuario.tenantName }}</div>
                  </div>
                  <div class="hidden md:flex md:flex-col md:items-end md:justify-center md:gap-1 md:min-w-[180px]">
                    <div class="text-sm text-gray-700">{{ usuario.lastLoginTime ? new Date(usuario.lastLoginTime).toLocaleString('es-ES', { hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Nunca' }}</div>
                    <div>
                      <span :class="usuario.isActive ? 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700' : 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-rose-100 text-rose-700'">{{ usuario.isActive ? 'Activo' : 'Inactivo' }}</span>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button @click="toggleStatus(usuario)" title="Toggle activo" class="p-2 rounded hover:bg-gray-100 text-sm">
                      <svg v-if="usuario.isActive" class="w-4 h-4 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                      <svg v-else class="w-4 h-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                    </button>
                    <button @click="editarUsuario(usuario)" title="Editar" class="p-2 rounded hover:bg-gray-100 text-sm">
                      <svg class="w-4 h-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5h6M12 11h6M7 17h10"/></svg>
                    </button>
                    <button @click="deleteModal(usuario.id)" title="Eliminar" class="p-2 rounded hover:bg-gray-100 text-sm">
                      <svg class="w-4 h-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="p-6 text-center text-gray-500">No hay usuarios para mostrar</div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- Pagination -->
      <div class="px-4 py-3 flex items-center justify-between border-t">
        <div class="text-xs text-gray-600">Mostrando {{ startIndex + 1 }} - {{ Math.min(endIndex, totalFiltered) }} de {{ totalFiltered }}</div>
        <div class="flex items-center gap-2">
          <button @click="changePage(currentPage-1)" :disabled="currentPage===1" class="px-2 py-1 rounded border bg-white disabled:opacity-50">Anterior</button>
          <div class="text-sm">Página {{ currentPage }} / {{ totalPages }}</div>
          <button @click="changePage(currentPage+1)" :disabled="currentPage===totalPages" class="px-2 py-1 rounded border bg-white disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>
  <Modal message="Usuario" @confirm="deleteUsuario" ref="modalComponent" />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import authService from "../../../composables/api/authService";
import userService from "../../../composables/api/userService";
import Modal from "../../modalConfirmar/ModalConfirmar.vue";

export default {
  name: 'usuarios',
  components: { Modal },
  setup() {
  const usuarios = ref([]);
  const showAll = ref(false);
  const sortField = ref('');
  const sortOrder = ref('asc');
  const search = ref('');
  const pageSize = ref(10);
  const currentPage = ref(1);
  const router = useRouter();
  const modalComponent = ref(null);

    // Función para calcular la fecha de vencimiento
    const calculateExpirationDate = (plan) => {
      const today = new Date();
      let expirationDate = new Date(today);

      switch (plan) {
        case 'Demo':
          // Una semana para la demo
          expirationDate.setDate(today.getDate() + 7);
          break;
        case '1 mes':
          expirationDate.setMonth(today.getMonth() + 1);
          break;
        case '2 meses':
          expirationDate.setMonth(today.getMonth() + 2);
          break;
        case '3 meses':
          expirationDate.setMonth(today.getMonth() + 3);
          break;
        case '1 año':
          expirationDate.setFullYear(today.getFullYear() + 1);
          break;
        default:
          return 'N/A';
      }
      return expirationDate.toLocaleDateString('es-ES');
    };



    const editarUsuario = (user) => {
      router.push({
        path: 'formUsuariosAdmin',
        query: { isAdmin: false, id: user.id },
      });
    };

    const deleteUsuario = async (usuarioId) => {
      try {
        await userService.removeUsuario(usuarioId);
      } catch (error) {
        console.error(error);
      }
      fetchUsuarios();
    };

    const deleteModal = (usuarioId) => {
      if (modalComponent.value) {
        modalComponent.value.getModal(usuarioId);
      } else {
        console.error("Modal component is not mounted or available.");
      }
    };

  const fetchUsuarios = async () => {
      try {
        const fetchedUsers = await authService.getUsers();
        usuarios.value = fetchedUsers.map(user => ({
          ...user,
          // Concatena nombre y apellido para la columna Nombre
          fullName: `${user.nombre || ''} ${user.apellido || ''}`.trim(),
          // Asigna 'telefono' a 'numeroTlf', o 'N/A' si no existe o es "0"
          numeroTlf: (user.telefono && user.telefono !== '0') ? user.telefono : 'N/A',
          estado: user.isActive ? 'Activo' : 'Inactivo', // Puedes ajustar la lógica de estado
          plan: user.plan || 'Demo', // Valor por defecto si no existe
          vencimiento: user.vencimiento || calculateExpirationDate(user.plan || 'Demo') // Calcula el vencimiento
        }));
      } catch (error) {
        console.error(error);
      }
    };

    // computed list with search + sort
    const filteredUsuariosComputed = computed(() => {
      let list = [...usuarios.value];
      // search
      const q = (search.value || '').trim().toLowerCase();
      if (q) {
        list = list.filter(u => {
          return (u.fullName || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.tenantName || '').toLowerCase().includes(q);
        });
      }
      // filter active
      list = showAll.value ? list : list.filter(u => u.isActive);
      // sort
      if (sortField.value) {
        list.sort((a, b) => {
          let result = 0;
          if (sortField.value === 'createdAt' || sortField.value === 'lastLoginTime' || sortField.value === 'vencimiento') {
            const dateA = a[sortField.value] ? new Date(a[sortField.value]) : new Date(0);
            const dateB = b[sortField.value] ? new Date(b[sortField.value]) : new Date(0);
            result = dateA - dateB;
          } else if (sortField.value === 'isActive') {
            result = (a[sortField.value] === b[sortField.value]) ? 0 : a[sortField.value] ? -1 : 1;
          } else if (sortField.value === 'fullName') {
            result = (a.fullName || '').localeCompare(b.fullName || '');
          } else {
            result = String(a[sortField.value] || '').localeCompare(String(b[sortField.value] || ''));
          }
          return sortOrder.value === 'asc' ? result : -result;
        });
      }
      return list;
    });

    const totalFiltered = computed(() => filteredUsuariosComputed.value.length);
    const totalPages = computed(() => Math.max(1, Math.ceil(totalFiltered.value / pageSize.value)));
    const startIndex = computed(() => (currentPage.value - 1) * pageSize.value);
    const endIndex = computed(() => startIndex.value + pageSize.value);
    const displayedUsuarios = computed(() => filteredUsuariosComputed.value.slice(startIndex.value, endIndex.value));

    const changePage = (p) => {
      if (p < 1) p = 1;
      if (p > totalPages.value) p = totalPages.value;
      currentPage.value = p;
    };

    const toggleStatus = async (usuario) => {
      try {
        const isActive = !usuario.isActive;
        await userService.toggleUserStatus(usuario.id, isActive);
        usuario.isActive = isActive;
        usuario.estado = isActive ? 'Activo' : 'Inactivo';
      } catch (error) {
        console.error(error);
      }
    };

    const updateUserPlan = (usuario) => {

      usuario.vencimiento = calculateExpirationDate(usuario.plan);
    };

    const sortUsuarios = (field) => {
      if (sortField.value === field) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortField.value = field;
        sortOrder.value = 'asc';
      }
    };

    const filteredUsuarios = computed(() => {
      let sortedUsers = [...usuarios.value];

      sortedUsers.sort((a, b) => {
        let result = 0;
        if (sortField.value) {
          if (sortField.value === 'createdAt' || sortField.value === 'lastLoginTime' || sortField.value === 'vencimiento') {
            const dateA = a[sortField.value] ? new Date(a[sortField.value]) : new Date(0); // Usar new Date(0) para fechas nulas/inválidas
            const dateB = b[sortField.value] ? new Date(b[sortField.value]) : new Date(0);
            result = dateA - dateB;
          } else if (sortField.value === 'isActive') {
            result = (a[sortField.value] === b[sortField.value]) ? 0 : a[sortField.value] ? -1 : 1; // Ordena booleanos
          } else if (sortField.value === 'fullName') {
            result = (a.fullName || '').localeCompare(b.fullName || '');
          }
          else {
            result = String(a[sortField.value]).localeCompare(String(b[sortField.value]));
          }
        }
        return sortOrder.value === 'asc' ? result : -result;
      });

      return showAll.value ? sortedUsers : sortedUsers.filter(usuario => usuario.isActive);
    });

    onMounted(() => {
      fetchUsuarios();
    });

    return {
      usuarios,
      showAll,
      filteredUsuarios: filteredUsuariosComputed,
      fetchUsuarios,
      editarUsuario,
      deleteUsuario,
      deleteModal,
      modalComponent,
      toggleStatus,
      sortField,
      sortOrder,
      sortUsuarios,
      updateUserPlan,
      search,
      pageSize,
      currentPage,
      totalPages,
      displayedUsuarios,
      changePage,
      totalFiltered,
      startIndex,
      endIndex
    };
  },
};
</script>

<style scoped></style>